import React, {useCallback, useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  TextField,
} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import {styled} from "styled-components";
import {Link} from "react-router";
import {Definition, DefinitionConfig} from "common/flowmaster/types/data_asset";
import {DataAssetReference} from "./configuration_inputs";
import GroupedSelector from "./grouped_selector";
import {getQueryParams} from "../containers/data_assets_create_view";
import {formatDate} from "common/utils/format_date";

import {OrganisationId} from "common/types/organisation";

const AssetContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  width: 100%;

  @media (max-width: 1920px) {
    flex-direction: column;
    align-items: start;
  }
`;

const styles = {
  container: {
    display: "flex",
    flex: 1,
    gap: "1em",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  selectorContainer: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    gap: ".5em",
    width: "100%",
  },
  selector: {
    flex: 1,
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  date: {
    fontSize: "0.8rem",
    color: "#757575",
    marginLeft: "8px",
  },
  outputKeyField: {
    width: "12em",
    flexShrink: 0,
  },
  versionSelector: {
    width: "150px",
    flexShrink: 0,
  },
};

const LATEST_ITEM = " ";

export const DisableColorLinkIcon = styled(LinkIcon)<{$disabled?: boolean}>`
  padding: 0;
  ${({$disabled}) => `
    color: ${$disabled ? "black" : "inherit"}
  `}
`;

interface DataAssetSelectorProps {
  definitions: Definition[];
  value: DataAssetReference;
  onChange: (value: DataAssetReference) => void;
  organisationId: OrganisationId;
  disabled?: boolean;
}

interface GroupedItem {
  groupId: string;
  latest: Definition | DefinitionConfig;
  items: Array<Definition | DefinitionConfig>;
}

const DataAssetSelector: React.FC<DataAssetSelectorProps> = ({
  definitions,
  value,
  onChange,
  organisationId,
  disabled,
}) => {
  const shouldIncludeArchivedConfig = useCallback(
    (
      config: DefinitionConfig,
      allConfigsInGroup: DefinitionConfig[],
    ): boolean => {
      if (!config.is_archived) {
        return true;
      }

      if (value.config_id === config.id.toString()) {
        return true;
      }

      if (!value.config_id && value.config_group_id === config.group_id) {
        const configsInGroup = allConfigsInGroup.filter(
          c => c.group_id === value.config_group_id,
        );
        return configsInGroup.every(c => c.is_archived);
      }

      return false;
    },
    [value],
  );

  const shouldIncludeArchivedDefinition = useCallback(
    (definition: Definition, allDefinitionsInGroup: Definition[]): boolean => {
      if (!definition.is_archived) {
        return true;
      }

      if (value.definition_id === definition.id.toString()) {
        return true;
      }

      if (
        !value.definition_id &&
        value.definition_group_id === definition.group_id
      ) {
        const definitionsInGroup = allDefinitionsInGroup.filter(
          d => d.group_id === value.definition_group_id,
        );
        return definitionsInGroup.every(d => d.is_archived);
      }

      return false;
    },
    [value],
  );

  const getGroupedItems = useCallback(
    (
      items: Definition[] | DefinitionConfig[],
    ): Record<string, Array<Definition | DefinitionConfig>> => {
      const grouped = items.reduce((acc, item) => {
        const groupId = item.group_id || item.id;
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(item);
        return acc;
      }, {});

      Object.keys(grouped).forEach(groupId => {
        grouped[groupId].sort(
          (a, b) =>
            new Date(b.creation_date || 0).getTime() -
            new Date(a.creation_date || 0).getTime(),
        );
      });

      return grouped;
    },
    [],
  );

  const getConfigurationsFromGroup = (
    definitionGroup: GroupedItem | undefined,
  ): DefinitionConfig[] => {
    if (!definitionGroup) {
      return [];
    }

    return definitionGroup.items.reduce(
      (acc: DefinitionConfig[], def: Definition) => {
        if (def.is_archived) {
          return acc;
        }

        if (def.workflow_instances?.length > 0) {
          acc.push(...def.workflow_instances);
        }
        return acc;
      },
      [],
    );
  };

  const definitionGroups = useMemo(() => {
    const groupedDefinitions = getGroupedItems(definitions);

    return Object.entries(groupedDefinitions)
      .filter(([, defs]) =>
        defs.some((def: Definition) => def.workflow_instances?.length > 0),
      )
      .map(([groupId, items]) => {
        const filteredItems = (items as Definition[]).filter(def =>
          shouldIncludeArchivedDefinition(def, items as Definition[]),
        );

        return {
          groupId,
          latest: filteredItems[0],
          items: filteredItems,
        };
      })
      .filter(group => group.items.length > 0)
      .sort((a, b) => a.latest.name.localeCompare(b.latest.name));
  }, [definitions, getGroupedItems, shouldIncludeArchivedDefinition]);

  const initialSelections = useMemo((): DataAssetReference => {
    let definitionGroupId = value.definition_group_id;
    const configGroupId = value.config_group_id;

    if (!definitionGroupId && value.definition_id) {
      const def = definitions.find(
        d => d.id.toString() === value.definition_id,
      );
      definitionGroupId = def?.group_id || def?.id.toString();
    }

    return {
      definition_group_id: definitionGroupId || "",
      definition_id: value.definition_id || "",
      config_group_id: configGroupId || "",
      config_id: value.config_id || "",
      run_id: value.run_id || "",
      output_key: value.output_key ?? "value",
    };
  }, [value, definitions]);

  const [selections, setSelections] = useState<DataAssetReference>(
    initialSelections,
  );

  useEffect(() => {
    if (!_.isEqual(initialSelections, selections)) {
      setSelections(initialSelections);
    }
  }, [initialSelections]);

  const currentDefinitionGroup = useMemo(() => {
    return definitionGroups.find(group =>
      selections.definition_group_id
        ? group.groupId === selections.definition_group_id
        : group.items.some(
            item => item.id.toString() === selections.definition_id,
          ),
    );
  }, [
    definitionGroups,
    selections.definition_group_id,
    selections.definition_id,
  ]);

  const configurations = useMemo((): DefinitionConfig[] => {
    if (!currentDefinitionGroup) {
      return [];
    }

    const allConfigs = selections.definition_id
      ? definitions.find(d => d.id.toString() === selections.definition_id)
          ?.workflow_instances || []
      : getConfigurationsFromGroup(currentDefinitionGroup);

    return allConfigs.filter(config =>
      shouldIncludeArchivedConfig(config, allConfigs),
    );
  }, [
    currentDefinitionGroup,
    selections.definition_id,
    definitions,
    shouldIncludeArchivedConfig,
  ]);

  const configurationGroups = useMemo(() => {
    const groupedConfigurations = getGroupedItems(configurations);

    return Object.keys(groupedConfigurations)
      .map(groupId => ({
        groupId,
        latest: groupedConfigurations[groupId][0],
        items: groupedConfigurations[groupId],
      }))
      .sort((a, b) => {
        const nameA = a.latest.name || "";
        const nameB = b.latest.name || "";
        return nameA.localeCompare(nameB);
      });
  }, [configurations, getGroupedItems]);

  const selectedConfigurationGroup = useMemo(() => {
    return configurationGroups.find(group =>
      selections.config_group_id
        ? group.groupId === selections.config_group_id
        : group.items.some(item => item.id.toString() === selections.config_id),
    );
  }, [configurationGroups, selections.config_group_id, selections.config_id]);

  const assets = useMemo(() => {
    const definitionId =
      selections.definition_id || currentDefinitionGroup?.latest.id;
    const configId =
      selections.config_id || selectedConfigurationGroup?.latest.id;

    const selectedDefinition = definitions.find(
      d => d.id.toString() === definitionId,
    );
    const selectedConfiguration = selectedDefinition?.workflow_instances?.find(
      c => c.id.toString() === configId,
    );

    return selectedConfiguration?.assets?.filter(asset => asset?.name) ?? [];
  }, [
    selections,
    currentDefinitionGroup,
    selectedConfigurationGroup,
    definitions,
  ]);

  const handleChange = useCallback(
    (field: keyof DataAssetReference, newValue: string) => {
      if (disabled) {
        return;
      }

      const newSelections = {...selections};

      switch (field) {
        case "definition_group_id": {
          newSelections.definition_group_id = newValue;
          newSelections.definition_id = "";
          newSelections.config_group_id = "";
          newSelections.config_id = "";
          newSelections.run_id = "";
          newSelections.output_key = "value";
          break;
        }
        case "definition_id": {
          newSelections.definition_id =
            newValue === LATEST_ITEM ? "" : newValue;
          newSelections.config_group_id = "";
          newSelections.config_id = "";
          newSelections.run_id = "";
          newSelections.output_key = "value";
          break;
        }
        case "config_group_id":
          newSelections.config_group_id = newValue;
          newSelections.config_id = "";
          newSelections.run_id = "";
          newSelections.output_key = "value";
          break;
        case "config_id":
          newSelections.config_id = newValue === LATEST_ITEM ? "" : newValue;
          newSelections.run_id = "";
          newSelections.output_key = "value";
          break;
        case "run_id":
          newSelections.run_id = newValue === LATEST_ITEM ? "" : newValue;
          break;
        case "output_key":
          newSelections.output_key = newValue;
          break;
      }

      setSelections(newSelections);
      onChange(newSelections);
    },
    [
      disabled,
      selections,
      definitionGroups,
      definitions,
      getGroupedItems,
      getConfigurationsFromGroup,
      onChange,
    ],
  );

  const emptyDefinitionSelection =
    value.definition_id === "" && !value.definition_group_id;
  const emptyConfigSelection = value.config_id === "" && !value.config_group_id;

  return (
    <Box style={styles.container}>
      <div style={{flex: 1, maxWidth: "40%"}}>
        <GroupedSelector
          label="Definition"
          groups={definitionGroups}
          selectedGroupId={selections.definition_group_id}
          selectedItemId={selections.definition_id}
          onGroupChange={value => handleChange("definition_group_id", value)}
          onItemChange={value => handleChange("definition_id", value)}
          disabled={disabled}
          linkTo={`/organisation/${organisationId}/workflow/${
            selections.definition_id || currentDefinitionGroup?.latest.id
          }/detail`}
          error={emptyDefinitionSelection}
          LATEST_ITEM={LATEST_ITEM}
        />
      </div>
      <div style={{flex: 1, maxWidth: "40%"}}>
        <GroupedSelector
          label="Configuration"
          groups={configurationGroups}
          selectedGroupId={selections.config_group_id}
          selectedItemId={selections.config_id}
          onGroupChange={value => handleChange("config_group_id", value)}
          onItemChange={value => handleChange("config_id", value)}
          disabled={!selections.definition_group_id || disabled}
          linkTo={`/organisation/${organisationId}/data-assets/definition/${
            selections.definition_id || currentDefinitionGroup?.latest.id
          }/config/${
            selections.config_id || selectedConfigurationGroup?.latest.id
          }${getQueryParams()}`}
          error={emptyConfigSelection}
          LATEST_ITEM={LATEST_ITEM}
        />
      </div>
      <div style={{flex: 1, maxWidth: "20%"}}>
        <AssetContainer>
          <div style={styles.selectorContainer}>
            <FormControl
              variant="outlined"
              size="small"
              style={styles.selector}
              disabled={!selections.config_group_id || disabled}
              fullWidth
            >
              <InputLabel>Asset</InputLabel>
              <Select
                value={selections.run_id || LATEST_ITEM}
                onChange={e => handleChange("run_id", e.target.value as string)}
                label="Asset"
                renderValue={selected => {
                  if (selected === LATEST_ITEM || !selected) {
                    return <em>Use latest</em>;
                  }
                  const asset = assets.find(a => a.id.toString() === selected);
                  return asset ? asset.name : <em>Use latest</em>;
                }}
              >
                <MenuItem value={LATEST_ITEM}>
                  <em>Use latest</em>
                </MenuItem>
                {assets.map(asset => (
                  <MenuItem key={asset.id} value={asset.id.toString()}>
                    <div style={styles.menuItemContent}>
                      <span>{asset.name}</span>
                      {asset.creation_date && (
                        <span style={styles.date}>
                          {formatDate(asset.creation_date)}
                        </span>
                      )}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(Boolean(selections.run_id) &&
              selections.run_id !== LATEST_ITEM) ||
              (true && (
                <Link
                  to={`/organisation/${organisationId}/llm_run/${selections.run_id}/detail`}
                >
                  <IconButton size="small" style={{padding: 0}}>
                    <DisableColorLinkIcon $disabled={disabled} />
                  </IconButton>
                </Link>
              ))}
          </div>

          <TextField
            variant="outlined"
            size="small"
            label="Output key"
            fullWidth
            style={styles.outputKeyField}
            value={selections.output_key}
            onChange={e => handleChange("output_key", e.target.value)}
            disabled={!selections.definition_group_id || disabled}
          />
        </AssetContainer>
      </div>
    </Box>
  );
};

export default DataAssetSelector;
